import { useSyncExternalStore } from 'react';

import type { DocumentSnapshot } from '../types';
import type { DocumentStore } from './documentStore';

/**
 * The React binding over `DocumentStore` (ADR-0016, docs/ARCHITECTURE.md §6).
 *
 * `useSyncExternalStore` is the supported way to read an external store: no tearing under
 * concurrent rendering, and no provider re-render cascade. It is also the reason the store
 * had to be written the way it was — `getSnapshot` must return a STABLE REFERENCE while
 * nothing observable has changed, which `DocumentStore.#publish` guarantees. If that
 * invariant ever breaks, this hook loops forever instead of failing quietly, and React says
 * so in the console.
 *
 * THE STORE ARRIVES AS AN ARGUMENT, never from a context read inside this file. Same reason
 * commands take the document as a parameter (ADR-0014): a Diff View has two stores, and two
 * panes calling `useDocument(storeA)` / `useDocument(storeB)` must not need a second hook.
 *
 * DERIVED VALUES ARE NOT COMPUTED HERE. A subscriber re-renders on every notification —
 * including every keystroke — so a projection like `problemList(snapshot.report)` belongs in
 * a `useMemo` keyed on `snapshot.version` in the component that needs it, not in a hook that
 * every consumer pays for.
 */
export function useDocument(store: DocumentStore): DocumentSnapshot {
  return useSyncExternalStore(
    (listener) => store.subscribe(listener),
    () => store.getSnapshot(),
    // The application is a client-only static SPA (ADR-0001) and never renders on a server.
    // The third argument is supplied anyway so a prerender pass cannot throw; the store is
    // constructible without a DOM, so the "server" snapshot is simply the real one.
    () => store.getSnapshot(),
  );
}
